import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/imageUtils';
import { getProducts } from '../services/api'; // Adicionado para busca live
import './Header.css';
import '../styles/theme.css';

const Header = () => {
  const { loadProducts } = useProducts();
  const { getTotalItems, toggleCart } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [liveResults, setLiveResults] = useState([]);
  const [showLiveResults, setShowLiveResults] = useState(false);
  const [isLoadingLiveSearch, setIsLoadingLiveSearch] = useState(false);
  const debounceTimer = useRef(null);

  // Atualizar estado do input sempre que a URL mudar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Lê 'termo' da página de pesquisa ou 'search' da home para manter o input sincronizado
    const term = params.get('termo') || params.get('search') || '';
    setSearchTerm(term);
    if (!term) {
      setLiveResults([]);
      setShowLiveResults(false);
    }
  }, [location.search, location.pathname]); // Re-executa ao mudar de página ou de parâmetros

  // Fechar menu ao redimensionar a janela
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Manipular envio do formulário de pesquisa
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm) {
      setIsMenuOpen(false); // Fecha o menu mobile se estiver aberto
      setShowLiveResults(false); // Esconde as sugestões ao vivo
      
      // Navega para a página de resultados de busca
      navigate(`/pesquisa?termo=${encodeURIComponent(trimmedSearchTerm)}`);
    }
  };
  
  // Real-time search temporarily disabled; use form submission only

  const fetchLiveSuggestions = async (currentSearchTerm) => {
    if (currentSearchTerm.length < 2) { // Mínimo 2 caracteres para buscar
      setLiveResults([]);
      setShowLiveResults(false);
      return;
    }
    setIsLoadingLiveSearch(true);
    try {
      const response = await getProducts(1, 5, null, currentSearchTerm, 'name', 'ASC'); // Busca 5 sugestões
      if (response && response.products) {
        setLiveResults(response.products);
        setShowLiveResults(response.products.length > 0);
      } else {
        setLiveResults([]);
        setShowLiveResults(false);
      }
    } catch (error) {
      console.error('Erro ao buscar sugestões ao vivo:', error);
      setLiveResults([]);
      setShowLiveResults(false);
    } finally {
      setIsLoadingLiveSearch(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (newSearchTerm.trim() === '') {
      setLiveResults([]);
      setShowLiveResults(false);
      return;
    }
    
    debounceTimer.current = setTimeout(() => {
      fetchLiveSuggestions(newSearchTerm);
    }, 300); // debounce de 300ms
  };

  // Alternar menu mobile
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);

  };



  return (
    <header className="header">
      <div className="container header-container">
        <div className="logo-container">
          <Link to="/" className="logo" onClick={() => loadProducts()}>
            <img 
              src="/logo/LOGO.png"
              alt="Floratta Cosméticos" 
              className="brand-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                // Fallback para texto caso a imagem não carregue
                e.target.parentNode.innerHTML = `
                  <span className="brand-name">FLORATTA</span>
                  <span className="brand-tagline">COSMÉTICOS</span>
                `;
              }}
            />
          </Link>
          <div className="mobile-controls">

            <div className="menu-button-container">
              <span className="menu-text-label">MENU</span>
              <button 
                className={`menu-toggle ${isMenuOpen ? 'active' : ''}`} 
                onClick={toggleMenu}
                aria-label="Menu"
              >
                <span className="menu-icon-bar"></span>
                <span className="menu-icon-bar"></span>
                <span className="menu-icon-bar"></span>
              </button>
            </div>
          </div>
        </div>

        <div className={`header-content ${isMenuOpen ? 'active' : ''}`}>
          <nav className="main-nav">
            <ul>
              {/* Campo de busca principal removido conforme solicitado */}
              {/* <li className="nav-search">
                <form onSubmit={handleSearch} className="search-form">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar produtos..."
                    className="search-input"
                  />
                  <button type="submit" className="search-button">
                    <i className="fas fa-search"></i>
                  </button>
                </form>
              </li> */}
              <li>
                <Link to="/" onClick={() => {
                  loadProducts();
                  setIsMenuOpen(false);
                }} className={window.location.pathname === '/' && !searchTerm ? 'active' : ''}>
                  <i className="fas fa-th"></i>
                  <span>Todos os Produtos</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/categoria/PRESENTES" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria
                    loadProducts(1, 20, 'PRESENTES');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }} 
                  className={window.location.pathname.includes('/categoria/PRESENTES') ? 'active' : ''}
                >
                  <i className="fas fa-gift"></i>
                  <span>PRESENTES</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/categoria/COSMÉTICOS" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria
                    loadProducts(1, 20, 'COSMÉTICOS');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }}
                  className={window.location.pathname.includes('/categoria/COSMÉTICOS') ? 'active' : ''}
                >
                  <i className="fas fa-spa"></i>
                  <span>COSMÉTICOS</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/categoria/PERFUMARIA" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria
                    loadProducts(1, 20, 'PERFUMARIA');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }}
                  className={window.location.pathname.includes('/categoria/PERFUMARIA') ? 'active' : ''}
                >
                  <i className="fas fa-air-freshener"></i>
                  <span>PERFUMARIA</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/categoria/MAQUIAGENS" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria
                    loadProducts(1, 20, 'MAQUIAGENS');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }}
                  className={window.location.pathname.includes('/categoria/MAQUIAGENS') ? 'active' : ''}
                >
                  <i className="fas fa-magic"></i>
                  <span>MAQUIAGENS</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/categoria/CONVENIENCIA" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria
                    loadProducts(1, 20, 'CONVENIENCIA');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }}
                  className={window.location.pathname.includes('/categoria/CONVENIENCIA') ? 'active' : ''}
                >
                  <i className="fas fa-store"></i>
                  <span>CONVENIÊNCIA</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/mega-oferta" 
                  onClick={() => {
                    // Limpar o termo de busca ao navegar para uma categoria
                    setSearchTerm('');
                    // Carregar produtos da categoria especial
                    loadProducts(1, 20, 'MEGA OFERTA');
                    // Fechar o menu móvel
                    setIsMenuOpen(false);
                  }}
                  className={window.location.pathname.includes('/mega-oferta') ? 'active' : ''}
                >
                  <i className="fas fa-percentage"></i>
                  <span>MEGA OFERTA</span>
                </Link>
              </li>
            </ul>
          </nav>

          <form className="search-form" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Buscar produtos..."
              value={searchTerm}
              onChange={handleSearchInputChange} // Modificado para busca live
              ref={searchInputRef}
            />
            <button type="submit" aria-label="Buscar">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </form>
          {showLiveResults && searchTerm && searchInputRef.current === document.activeElement && (
            <div className="live-search-results live-search-results-desktop">
              {isLoadingLiveSearch && <div className="live-search-loading">Buscando...</div>}
              {!isLoadingLiveSearch && liveResults.length === 0 && searchTerm.length >= 2 && (
                <div className="live-search-no-results">Nenhum resultado.</div>
              )}
              <ul>
                {liveResults.map(product => (
                  <li key={`desktop-${product.id || product.code}`}>
                    <Link 
                      to={`/produto/${product.code}`}
                      onClick={() => {
                        setShowLiveResults(false);
                      }}
                    >
                      {product.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="cart-icon-wrapper" onClick={toggleCart}>
            <div className="cart-icon">
              <i className="fas fa-shopping-cart"></i>
              {getTotalItems() > 0 && (
                <span className="cart-badge">{getTotalItems()}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Formulário de busca móvel (sempre visível) */}
      <div className="mobile-search-container">
        <form className="mobile-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={handleSearchInputChange} // Modificado para busca live
          />
          <button type="submit" aria-label="Buscar">
            <i className="fas fa-search"></i>
          </button>
        </form>
        {showLiveResults && searchTerm && (
          <div className="live-search-results">
            {isLoadingLiveSearch && <div className="live-search-loading">Buscando...</div>}
            {!isLoadingLiveSearch && liveResults.length === 0 && searchTerm.length >= 2 && (
              <div className="live-search-no-results">Nenhum resultado.</div>
            )}
            <ul>
              {liveResults.map(product => (
                <li key={`mobile-${product.id || product.code}`}>
                  <Link 
                    to={`/produto/${product.code}`}
                    onClick={() => {
                      setShowLiveResults(false); 
                    }}
                  >
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
